import { useNavigate, useParams } from 'react-router-dom';
import { ErrorMessage } from 'nhsuk-react-components';
import { useState } from 'react';
import useCampaign from 'hooks/useCampaign.ts';
import RoutingIDsForm from 'components/forms/RoutingIDs/RoutingIDs.tsx';
import useProtectRoute from 'hooks/useProtectRoute.ts';
import ApiErrorDetails from 'components/ApiErrorDetails/index.tsx';
import { getIterationAndRuleByIDs } from 'utils/ruleUtils.ts';
import useUpdateIteration from 'queries/mutate/updateIteration.ts';
import { IterationDetailsFormValues } from 'schemas/Iteration.schema.ts';
import { getIterationById } from 'utils/iterationUtils.ts';
import useFetchSpecificCampaign from 'queries/fetch/fetchSpecificCampaign.ts';
import useFetchTemplateForCampaign from 'queries/fetch/fetchTemplateForCampaign.ts';
import { ApiError } from 'schemas/ApiError.schema';
import { IRuleLevelParams } from 'utils/parameterUtils';
import useCampaignNavigate from 'hooks/useCampaignNavigate';
import useFetchTeamRoutingPlans from 'queries/fetch/fetchTeamRoutingPlans';
import featureFlags from 'services/featureFlags';
import Back from 'components/Back';

export default function RoutingIDsPage() {
  const {
    teamId, campaignId, iterationId, ruleId,
  } = useParams() as IRuleLevelParams;

  const { campaign } = useCampaign();
  const { rule: iterationRule } = getIterationAndRuleByIDs(campaign, iterationId, ruleId);
  const [apiError, setApiError] = useState<ApiError | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const updateIteration = useUpdateIteration();
  const { navigateToCampaign } = useCampaignNavigate();
  const navigate = useNavigate();
  const fetchSpecificCampaign = useFetchSpecificCampaign({ teamId, campaignId });
  useProtectRoute();
  const { data: campaignTemplate } = useFetchTemplateForCampaign({
    teamId,
    campaignId,
    isEnabled: Boolean(campaign.CampaignConfig.Template),
  });

  const { data: teamRoutingPlans } = useFetchTeamRoutingPlans({ teamId, enabled: featureFlags.notifyRoutingPlansEnabled });

  const onSuccessfulSubmission = async (routingPlan: string) => {
    setApiError(null);

    const currentIteration = getIterationById(campaign, iterationId!);
    const updatedIteration = {
      ...currentIteration,
      IterationRules: currentIteration?.IterationRules?.map((rule) => {
        if (rule.Priority === iterationRule?.Priority && rule.Type === iterationRule?.Type) {
          return { ...rule, CommsRouting: routingPlan };
        }
        return rule;
      }),
    };

    try {
      setIsSubmitting(true);
      await updateIteration.mutateAsync({
        campaignId,
        iteration: updatedIteration as IterationDetailsFormValues,
        iterationId,
        teamId,
      });

      await fetchSpecificCampaign.refetch();
      navigateToCampaign(`iteration/${iterationId}/summary?openRules=R`);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'An error occurred saving the message plan';
      setApiError({ message });
    } finally {
      setIsSubmitting(false);
    }
  };

  const routingPlan = iterationRule?.CommsRouting || '';
  const existingRoutingIDs = routingPlan.split('|').filter((id) => id !== '');

  if (!iterationId || !iterationRule?.Name) {
    return (
      <ErrorMessage>
        Could not find iteration
      </ErrorMessage>
    );
  }

  if (campaignTemplate?.['IterationDetails.IterationRules.routing.input.display'] === 'hidden') {
    navigate(`/${teamId}/campaign/${campaignId}/iteration/${iterationId}/summary`);
    return null;
  }

  return (
    <div>
      <div className="nhsuk-u-margin-bottom-6">
        <Back />
      </div>

      <h1 role="main" id="maincontent">
        Add message plans for
        {' '}
        {iterationRule?.Name}
      </h1>
      <p>
        A message plan is used by NHS Notify and Select and Invite. It
        links a communication request to one or more message plans.
      </p>
      <RoutingIDsForm
        onSuccessfulSubmission={onSuccessfulSubmission}
        existingRoutingIDs={existingRoutingIDs}
        isSubmitting={isSubmitting}
        teamRoutingPlans={teamRoutingPlans || []}
      />
      <ApiErrorDetails apiError={apiError} />
    </div>
  );
}
